// GENERATED FILE. DO NOT EDIT.
//
// One method per operation on the public 'api' surface, generated from
// the mountayaapp/schemas contract (openapi/api.yaml).
//
// Method names are the spec's operationIds verbatim.
// Edit the spec in mountayaapp/schemas, not this file.

import type { BinaryBody, RequestOptions, MountayaTransport } from "../core/rules/transport.js";
import type { operations } from "./schema.gen.js";
import { agentRunDates, collectionDates, invoiceDates, itineraryDates, itineraryWithDetailsDates, membershipDates, organizationDates, organizationInvitationDates, organizationMemberDates, organizationSubscriptionSummaryDates, publishableKeyDates, sessionDates, shareLinkDates, userInvitationDates, zoneDates } from "./dates.gen.js";

/**
 * SuccessBody extracts the JSON body of an operation's 2xx response: its 200, else its 201,
 * else its 202.
 *
 * The platform wraps every success in an envelope whose `data` carries the payload, so
 * this resolves to the envelope rather than to the bare resource: callers read
 * `result.data`, which is what the wire actually sends, `null` on a write that removes
 * something and has nothing left to answer. A 202 is read like the other two: an operation
 * that starts work rather than finishing it names what it started at `data`, and that is
 * typed only if this reaches it.
 */
type SuccessBody<O> = O extends {
  responses: { 200: { content: { "application/json": infer B } } };
}
  ? B
  : O extends { responses: { 201: { content: { "application/json": infer C } } } }
    ? C
    : O extends { responses: { 202: { content: { "application/json": infer D } } } }
      ? D
      : unknown;

/**
 * Delete a collection.
 * DELETE /v1/collections/{collection_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteCollection = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteCollection"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteCollection",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Remove an itinerary from a collection.
 * DELETE /v1/collections/{collection_id}/itineraries/{itinerary_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteCollectionItinerary = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteCollectionItinerary"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteCollectionItinerary",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/itineraries/${encodeURIComponent(String(args.itinerary_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Remove the collection's share link.
 * DELETE /v1/collections/{collection_id}/share-link
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteCollectionShareLink = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteCollectionShareLink"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteCollectionShareLink",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/share-link`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Remove a zone from a collection.
 * DELETE /v1/collections/{collection_id}/zones/{zone_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteCollectionZone = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly "zone_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteCollectionZone"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteCollectionZone",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/zones/${encodeURIComponent(String(args.zone_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Delete an itinerary.
 * DELETE /v1/itineraries/{itinerary_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteItinerary = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteItinerary"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteItinerary",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Take the itinerary's publication down.
 * DELETE /v1/itineraries/{itinerary_id}/publication
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteItineraryPublication = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteItineraryPublication"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteItineraryPublication",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/publication`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Delete a segment from an itinerary.
 * DELETE /v1/itineraries/{itinerary_id}/segments/{segment_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteItinerarySegment = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "segment_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteItinerarySegment"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteItinerarySegment",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segments/${encodeURIComponent(String(args.segment_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Delete a segment group.
 * DELETE /v1/itineraries/{itinerary_id}/segment-groups/{group_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteItinerarySegmentGroup = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "group_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteItinerarySegmentGroup"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteItinerarySegmentGroup",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segment-groups/${encodeURIComponent(String(args.group_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Remove the itinerary's share link.
 * DELETE /v1/itineraries/{itinerary_id}/share-link
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteItineraryShareLink = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteItineraryShareLink"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteItineraryShareLink",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/share-link`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Delete a waypoint from an itinerary.
 * DELETE /v1/itineraries/{itinerary_id}/waypoints/{waypoint_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteItineraryWaypoint = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "waypoint_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteItineraryWaypoint"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteItineraryWaypoint",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/waypoints/${encodeURIComponent(String(args.waypoint_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Decline an invitation.
 * DELETE /v1/me/invitations/{invitation_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteMyInvitation = (transport: MountayaTransport, args: {
  readonly "invitation_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteMyInvitation"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteMyInvitation",
    path: `/v1/me/invitations/${encodeURIComponent(String(args.invitation_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Delete an organization's AI provider configuration.
 * DELETE /v1/organizations/{organization_id}/ai-provider-config
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteOrganizationAiProviderConfig = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteOrganizationAiProviderConfig"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteOrganizationAiProviderConfig",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/ai-provider-config`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Detach a payment method from an organization.
 * DELETE /v1/organizations/{organization_id}/billing/payment-methods/{payment_method_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteOrganizationBillingPaymentMethod = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "payment_method_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteOrganizationBillingPaymentMethod"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteOrganizationBillingPaymentMethod",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/payment-methods/${encodeURIComponent(String(args.payment_method_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * End an organization's Data API subscription at the end of its period.
 * DELETE /v1/organizations/{organization_id}/billing/subscription
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteOrganizationBillingSubscription = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteOrganizationBillingSubscription"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: organizationSubscriptionSummaryDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteOrganizationBillingSubscription",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/subscription`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Withdraw a pending invitation.
 * DELETE /v1/organizations/{organization_id}/invitations/{invitation_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteOrganizationInvitation = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "invitation_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteOrganizationInvitation"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteOrganizationInvitation",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/invitations/${encodeURIComponent(String(args.invitation_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Remove a member from an organization, or leave it.
 * DELETE /v1/organizations/{organization_id}/members/{user_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteOrganizationMember = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "user_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteOrganizationMember"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "DELETE",
    operationId: "deleteOrganizationMember",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/members/${encodeURIComponent(String(args.user_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Revoke a publishable key.
 * DELETE /v1/organizations/{organization_id}/publishable-keys/{key_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteOrganizationPublishableKey = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "key_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteOrganizationPublishableKey"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: publishableKeyDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteOrganizationPublishableKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/publishable-keys/${encodeURIComponent(String(args.key_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Delete a zone.
 * DELETE /v1/zones/{zone_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteZone = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteZone"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteZone",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Take the zone's publication down.
 * DELETE /v1/zones/{zone_id}/publication
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const deleteZonePublication = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["deleteZonePublication"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: zoneDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "DELETE",
    operationId: "deleteZonePublication",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}/publication`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * List all activity categories and their activities.
 * GET /v1/activities
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getActivities = (transport: MountayaTransport, options?: RequestOptions): Promise<SuccessBody<operations["getActivities"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getActivities",
    path: "/v1/activities",
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Stream an agent run's events over Server-Sent Events.
 * GET /v1/agent/runs/{run_id}/events
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getAgentRunEvents = (transport: MountayaTransport, args: {
  readonly "run_id": string;
}, options?: RequestOptions): Promise<BinaryBody> =>
  transport.requestBinary({
    accept: "text/event-stream",
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getAgentRunEvents",
    path: `/v1/agent/runs/${encodeURIComponent(String(args.run_id))}/events`,
    requiresSession: true,
    retry: options?.retry,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the AI provider catalog.
 * GET /v1/ai-providers
 * Requires a publishable key with the `management` scope.
 */
export const getAiProviders = (transport: MountayaTransport, options?: RequestOptions): Promise<SuccessBody<operations["getAiProviders"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getAiProviders",
    path: "/v1/ai-providers",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the billing catalog.
 * GET /v1/billing/products
 * Requires a publishable key with the `management` scope.
 */
export const getBillingProducts = (transport: MountayaTransport, options?: RequestOptions): Promise<SuccessBody<operations["getBillingProducts"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getBillingProducts",
    path: "/v1/billing/products",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve a single collection with its itineraries and zones.
 * GET /v1/collections/{collection_id}
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getCollection = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getCollection"]>> =>
  transport.request({
    acceptsShareToken: true,
    dates: { data: collectionDates },
    idempotent: true,
    method: "GET",
    operationId: "getCollection",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the itineraries a collection contains.
 * GET /v1/collections/{collection_id}/itineraries
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getCollectionItineraries = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly query?: NonNullable<operations["getCollectionItineraries"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getCollectionItineraries"]>> =>
  transport.request({
    acceptsShareToken: true,
    dates: { data: [itineraryDates] },
    idempotent: true,
    method: "GET",
    operationId: "getCollectionItineraries",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/itineraries`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List collections.
 * GET /v1/collections
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getCollections = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getCollections"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getCollections"]>> =>
  transport.request({
    dates: { data: [collectionDates] },
    idempotent: true,
    method: "GET",
    operationId: "getCollections",
    path: "/v1/collections",
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Read the collection's share link.
 * GET /v1/collections/{collection_id}/share-link
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getCollectionShareLink = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getCollectionShareLink"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: shareLinkDates },
    idempotent: true,
    method: "GET",
    operationId: "getCollectionShareLink",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/share-link`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the zones a collection contains.
 * GET /v1/collections/{collection_id}/zones
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getCollectionZones = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly query?: NonNullable<operations["getCollectionZones"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getCollectionZones"]>> =>
  transport.request({
    acceptsShareToken: true,
    dates: { data: [zoneDates] },
    idempotent: true,
    method: "GET",
    operationId: "getCollectionZones",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/zones`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the itineraries the credential reaches.
 * GET /v1/itineraries
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getItineraries = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getItineraries"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getItineraries"]>> =>
  transport.request({
    dates: { data: [itineraryDates] },
    idempotent: true,
    method: "GET",
    operationId: "getItineraries",
    path: "/v1/itineraries",
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve a single itinerary with its segments and waypoints.
 * GET /v1/itineraries/{itinerary_id}
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getItinerary = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly query?: NonNullable<operations["getItinerary"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getItinerary"]>> =>
  transport.request({
    acceptsShareToken: true,
    dates: { data: itineraryWithDetailsDates },
    idempotent: true,
    method: "GET",
    operationId: "getItinerary",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Stream one itinerary's collaboration session over SSE.
 * GET /v1/itineraries/{itinerary_id}/events
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getItineraryEvents = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
}, options?: RequestOptions): Promise<BinaryBody> =>
  transport.requestBinary({
    accept: "text/event-stream",
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getItineraryEvents",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/events`,
    requiresSession: true,
    retry: options?.retry,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Export an itinerary as a downloadable file.
 * GET /v1/itineraries/{itinerary_id}/export
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getItineraryExport = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly query?: NonNullable<operations["getItineraryExport"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<BinaryBody> =>
  transport.requestBinary({
    accept: "application/gpx+xml, application/geo+json, application/vnd.google-earth.kml+xml, application/vnd.google-earth.kmz, application/vnd.ant.fit, application/vnd.garmin.tcx+xml, text/csv",
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getItineraryExport",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/export`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Re-time an itinerary at a travel pace the caller names.
 * GET /v1/itineraries/{itinerary_id}/pace
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getItineraryPace = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly query?: NonNullable<operations["getItineraryPace"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getItineraryPace"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getItineraryPace",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/pace`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Read the itinerary's share link.
 * GET /v1/itineraries/{itinerary_id}/share-link
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getItineraryShareLink = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getItineraryShareLink"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: shareLinkDates },
    idempotent: true,
    method: "GET",
    operationId: "getItineraryShareLink",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/share-link`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List terrains, overlays, and the zone layer.
 * GET /v1/layers
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getLayers = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getLayers"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getLayers"]>> =>
  transport.request({
    acceptsShareToken: true,
    idempotent: true,
    method: "GET",
    operationId: "getLayers",
    path: "/v1/layers",
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the invitations waiting for the acting user.
 * GET /v1/me/invitations
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getMyInvitations = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getMyInvitations"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getMyInvitations"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: [userInvitationDates] },
    idempotent: true,
    method: "GET",
    operationId: "getMyInvitations",
    path: "/v1/me/invitations",
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List the acting user's memberships.
 * GET /v1/me/organizations
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getMyOrganizations = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getMyOrganizations"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getMyOrganizations"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: [membershipDates] },
    idempotent: true,
    method: "GET",
    operationId: "getMyOrganizations",
    path: "/v1/me/organizations",
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve the acting user's display preferences.
 * GET /v1/me/preferences
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getMyPreferences = (transport: MountayaTransport, options?: RequestOptions): Promise<SuccessBody<operations["getMyPreferences"]>> =>
  transport.request({
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getMyPreferences",
    path: "/v1/me/preferences",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization.
 * GET /v1/organizations/{organization_id}
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getOrganization = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganization"]>> =>
  transport.request({
    acceptsShareToken: true,
    dates: { data: organizationDates },
    idempotent: true,
    method: "GET",
    operationId: "getOrganization",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}`,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's AI provider configuration.
 * GET /v1/organizations/{organization_id}/ai-provider-config
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationAiProviderConfig = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationAiProviderConfig"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationAiProviderConfig",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/ai-provider-config`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's billing information.
 * GET /v1/organizations/{organization_id}/billing/info
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationBillingInfo = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationBillingInfo"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationBillingInfo",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/info`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List an organization's invoices.
 * GET /v1/organizations/{organization_id}/billing/invoices
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationBillingInvoices = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationBillingInvoices"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationBillingInvoices"]>> =>
  transport.request({
    dates: { data: [invoiceDates] },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationBillingInvoices",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/invoices`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List an organization's saved payment methods.
 * GET /v1/organizations/{organization_id}/billing/payment-methods
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationBillingPaymentMethods = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationBillingPaymentMethods"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationBillingPaymentMethods"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationBillingPaymentMethods",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/payment-methods`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Check what a promotion code is worth to an organization.
 * GET /v1/organizations/{organization_id}/billing/promotion-codes/{code}
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationBillingPromotionCode = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "code": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationBillingPromotionCode"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationBillingPromotionCode",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/promotion-codes/${encodeURIComponent(String(args.code))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's subscription.
 * GET /v1/organizations/{organization_id}/billing/subscription
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationBillingSubscription = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationBillingSubscription"]>> =>
  transport.request({
    dates: { data: organizationSubscriptionSummaryDates },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationBillingSubscription",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/subscription`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Stream an organization's list events over SSE.
 * GET /v1/organizations/{organization_id}/events
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getOrganizationEvents = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
}, options?: RequestOptions): Promise<BinaryBody> =>
  transport.requestBinary({
    accept: "text/event-stream",
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationEvents",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/events`,
    requiresSession: true,
    retry: options?.retry,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve a pending invitation.
 * GET /v1/organizations/{organization_id}/invitations/{invitation_id}
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationInvitation = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "invitation_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationInvitation"]>> =>
  transport.request({
    dates: { data: organizationInvitationDates },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationInvitation",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/invitations/${encodeURIComponent(String(args.invitation_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List an organization's pending invitations.
 * GET /v1/organizations/{organization_id}/invitations
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationInvitations = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationInvitations"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationInvitations"]>> =>
  transport.request({
    dates: { data: [organizationInvitationDates] },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationInvitations",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/invitations`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List an organization's members.
 * GET /v1/organizations/{organization_id}/members
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationMembers = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationMembers"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationMembers"]>> =>
  transport.request({
    dates: { data: [organizationMemberDates] },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationMembers",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/members`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve a publishable key.
 * GET /v1/organizations/{organization_id}/publishable-keys/{key_id}
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationPublishableKey = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "key_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationPublishableKey"]>> =>
  transport.request({
    dates: { data: publishableKeyDates },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationPublishableKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/publishable-keys/${encodeURIComponent(String(args.key_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List an organization's publishable keys.
 * GET /v1/organizations/{organization_id}/publishable-keys
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationPublishableKeys = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationPublishableKeys"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationPublishableKeys"]>> =>
  transport.request({
    dates: { data: [publishableKeyDates] },
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationPublishableKeys",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/publishable-keys`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's Map Publishing settings.
 * GET /v1/organizations/{organization_id}/settings
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationSettings = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationSettings"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationSettings",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/settings`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's data usage.
 * GET /v1/organizations/{organization_id}/usage/data
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationUsageData = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationUsageData"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationUsageData"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationUsageData",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/usage/data`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve an organization's Tile API usage.
 * GET /v1/organizations/{organization_id}/usage/tiles
 * Requires a publishable key with the `management` scope.
 */
export const getOrganizationUsageTiles = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly query?: NonNullable<operations["getOrganizationUsageTiles"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getOrganizationUsageTiles"]>> =>
  transport.request({
    idempotent: true,
    method: "GET",
    operationId: "getOrganizationUsageTiles",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/usage/tiles`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Retrieve a single zone with its geometry.
 * GET /v1/zones/{zone_id}
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getZone = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
  readonly query?: NonNullable<operations["getZone"]["parameters"]["query"]> | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["getZone"]>> =>
  transport.request({
    acceptsShareToken: true,
    dates: { data: zoneDates },
    idempotent: true,
    method: "GET",
    operationId: "getZone",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}`,
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Stream one zone's collaboration session over SSE.
 * GET /v1/zones/{zone_id}/events
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const getZoneEvents = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
}, options?: RequestOptions): Promise<BinaryBody> =>
  transport.requestBinary({
    accept: "text/event-stream",
    actsAsMember: true,
    idempotent: true,
    method: "GET",
    operationId: "getZoneEvents",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}/events`,
    requiresSession: true,
    retry: options?.retry,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * List zones.
 * GET /v1/zones
 * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
 */
export const getZones = (transport: MountayaTransport, args: {
  readonly query?: NonNullable<operations["getZones"]["parameters"]["query"]> | undefined;
} = {}, options?: RequestOptions): Promise<SuccessBody<operations["getZones"]>> =>
  transport.request({
    dates: { data: [zoneDates] },
    idempotent: true,
    method: "GET",
    operationId: "getZones",
    path: "/v1/zones",
    query: args.query,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an existing collection.
 * PATCH /v1/collections/{collection_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchCollection = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly body: operations["patchCollection"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchCollection"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchCollection",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Set an itinerary's position within a collection.
 * PATCH /v1/collections/{collection_id}/itineraries/{itinerary_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchCollectionItinerary = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly "itinerary_id": string;
  readonly body: operations["patchCollectionItinerary"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchCollectionItinerary"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchCollectionItinerary",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/itineraries/${encodeURIComponent(String(args.itinerary_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an existing itinerary.
 * PATCH /v1/itineraries/{itinerary_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchItinerary = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly body: operations["patchItinerary"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchItinerary"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchItinerary",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an existing segment.
 * PATCH /v1/itineraries/{itinerary_id}/segments/{segment_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchItinerarySegment = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "segment_id": string;
  readonly body: operations["patchItinerarySegment"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchItinerarySegment"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchItinerarySegment",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segments/${encodeURIComponent(String(args.segment_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update a segment group.
 * PATCH /v1/itineraries/{itinerary_id}/segment-groups/{group_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchItinerarySegmentGroup = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "group_id": string;
  readonly body: operations["patchItinerarySegmentGroup"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchItinerarySegmentGroup"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchItinerarySegmentGroup",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segment-groups/${encodeURIComponent(String(args.group_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an existing waypoint.
 * PATCH /v1/itineraries/{itinerary_id}/waypoints/{waypoint_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchItineraryWaypoint = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "waypoint_id": string;
  readonly body: operations["patchItineraryWaypoint"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchItineraryWaypoint"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchItineraryWaypoint",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/waypoints/${encodeURIComponent(String(args.waypoint_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update the acting user's display preferences.
 * PATCH /v1/me/preferences
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchMyPreferences = (transport: MountayaTransport, args: {
  readonly body: operations["patchMyPreferences"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchMyPreferences"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "PATCH",
    operationId: "patchMyPreferences",
    path: "/v1/me/preferences",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an organization.
 * PATCH /v1/organizations/{organization_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchOrganization = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["patchOrganization"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchOrganization"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchOrganization",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an organization's AI provider configuration.
 * PATCH /v1/organizations/{organization_id}/ai-provider-config
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchOrganizationAiProviderConfig = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["patchOrganizationAiProviderConfig"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchOrganizationAiProviderConfig"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchOrganizationAiProviderConfig",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/ai-provider-config`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an organization's billing information.
 * PATCH /v1/organizations/{organization_id}/billing/info
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchOrganizationBillingInfo = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["patchOrganizationBillingInfo"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchOrganizationBillingInfo"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "PATCH",
    operationId: "patchOrganizationBillingInfo",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/info`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Change a member's role.
 * PATCH /v1/organizations/{organization_id}/members/{user_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchOrganizationMember = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "user_id": string;
  readonly body: operations["patchOrganizationMember"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchOrganizationMember"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationMemberDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "PATCH",
    operationId: "patchOrganizationMember",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/members/${encodeURIComponent(String(args.user_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update a publishable key's name or scopes.
 * PATCH /v1/organizations/{organization_id}/publishable-keys/{key_id}
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchOrganizationPublishableKey = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "key_id": string;
  readonly body: operations["patchOrganizationPublishableKey"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchOrganizationPublishableKey"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: publishableKeyDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchOrganizationPublishableKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/publishable-keys/${encodeURIComponent(String(args.key_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an organization's Map Publishing settings.
 * PATCH /v1/organizations/{organization_id}/settings
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchOrganizationSettings = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["patchOrganizationSettings"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchOrganizationSettings"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchOrganizationSettings",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/settings`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Update an existing zone.
 * PATCH /v1/zones/{zone_id}
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const patchZone = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
  readonly body: operations["patchZone"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["patchZone"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: zoneDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "PATCH",
    operationId: "patchZone",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Start a new agent run.
 * POST /v1/agent/runs
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postAgentRun = (transport: MountayaTransport, args: {
  readonly body: operations["postAgentRun"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postAgentRun"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: agentRunDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postAgentRun",
    path: "/v1/agent/runs",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Record the user's rating of one assistant message in a run.
 * POST /v1/agent/runs/{run_id}/feedback
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postAgentRunFeedback = (transport: MountayaTransport, args: {
  readonly "run_id": string;
  readonly body: operations["postAgentRunFeedback"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postAgentRunFeedback"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postAgentRunFeedback",
    path: `/v1/agent/runs/${encodeURIComponent(String(args.run_id))}/feedback`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Send an input signal into a running agent run.
 * POST /v1/agent/runs/{run_id}/input
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postAgentRunInput = (transport: MountayaTransport, args: {
  readonly "run_id": string;
  readonly body: operations["postAgentRunInput"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postAgentRunInput"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postAgentRunInput",
    path: `/v1/agent/runs/${encodeURIComponent(String(args.run_id))}/input`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a new collection.
 * POST /v1/collections
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postCollection = (transport: MountayaTransport, args: {
  readonly body: operations["postCollection"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postCollection"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postCollection",
    path: "/v1/collections",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Add itineraries to a collection.
 * POST /v1/collections/{collection_id}/itineraries
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postCollectionItineraries = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly body: operations["postCollectionItineraries"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postCollectionItineraries"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postCollectionItineraries",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/itineraries`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Mint the collection's share link, or return the one it has.
 * POST /v1/collections/{collection_id}/share-link
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postCollectionShareLink = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postCollectionShareLink"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: shareLinkDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postCollectionShareLink",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/share-link`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Rotate the collection's share link.
 * POST /v1/collections/{collection_id}/share-link/rotate
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postCollectionShareLinkRotate = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postCollectionShareLinkRotate"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: shareLinkDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postCollectionShareLinkRotate",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/share-link/rotate`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Add zones to a collection.
 * POST /v1/collections/{collection_id}/zones
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postCollectionZones = (transport: MountayaTransport, args: {
  readonly "collection_id": string;
  readonly body: operations["postCollectionZones"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postCollectionZones"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: collectionDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postCollectionZones",
    path: `/v1/collections/${encodeURIComponent(String(args.collection_id))}/zones`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a new itinerary.
 * POST /v1/itineraries
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItinerary = (transport: MountayaTransport, args: {
  readonly body: operations["postItinerary"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItinerary"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postItinerary",
    path: "/v1/itineraries",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Send a collaboration signal into an itinerary's session.
 * POST /v1/itineraries/{itinerary_id}/collab
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryCollab = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly body: operations["postItineraryCollab"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryCollab"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotent: true,
    method: "POST",
    operationId: "postItineraryCollab",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/collab`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Duplicate an itinerary into the key's organization.
 * POST /v1/itineraries/{itinerary_id}/duplicate
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryDuplicate = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly query?: NonNullable<operations["postItineraryDuplicate"]["parameters"]["query"]> | undefined;
  readonly body: operations["postItineraryDuplicate"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryDuplicate"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postItineraryDuplicate",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/duplicate`,
    query: args.query,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Confirm an itinerary import, persisting the reviewed draft.
 * POST /v1/itineraries/import
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryImport = (transport: MountayaTransport, args: {
  readonly body: operations["postItineraryImport"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryImport"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postItineraryImport",
    path: "/v1/itineraries/import",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 55000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Preview an itinerary import without persisting it.
 * POST /v1/itineraries/preview
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryPreview = (transport: MountayaTransport, args: {
  readonly body: FormData;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryPreview"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postItineraryPreview",
    path: "/v1/itineraries/preview",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 55000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Publish the itinerary's current draft.
 * POST /v1/itineraries/{itinerary_id}/publication
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryPublication = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryPublication"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItineraryPublication",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/publication`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a segment, at the end of the chain or before another.
 * POST /v1/itineraries/{itinerary_id}/segments
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItinerarySegment = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly body: operations["postItinerarySegment"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItinerarySegment"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItinerarySegment",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segments`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Convert a segment between routed and freehand.
 * POST /v1/itineraries/{itinerary_id}/segments/{segment_id}/convert
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItinerarySegmentConvert = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "segment_id": string;
  readonly body: operations["postItinerarySegmentConvert"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItinerarySegmentConvert"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItinerarySegmentConvert",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segments/${encodeURIComponent(String(args.segment_id))}/convert`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Group a contiguous run of an itinerary's segments.
 * POST /v1/itineraries/{itinerary_id}/segment-groups
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItinerarySegmentGroup = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly body: operations["postItinerarySegmentGroup"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItinerarySegmentGroup"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItinerarySegmentGroup",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segment-groups`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Merge a segment with the one that follows it.
 * POST /v1/itineraries/{itinerary_id}/segments/{segment_id}/merge
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItinerarySegmentMerge = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "segment_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItinerarySegmentMerge"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItinerarySegmentMerge",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segments/${encodeURIComponent(String(args.segment_id))}/merge`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Split a segment in two at a fraction of its length.
 * POST /v1/itineraries/{itinerary_id}/segments/{segment_id}/split
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItinerarySegmentSplit = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly "segment_id": string;
  readonly body: operations["postItinerarySegmentSplit"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItinerarySegmentSplit"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItinerarySegmentSplit",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/segments/${encodeURIComponent(String(args.segment_id))}/split`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Mint the itinerary's share link, or return the one it has.
 * POST /v1/itineraries/{itinerary_id}/share-link
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryShareLink = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryShareLink"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: shareLinkDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postItineraryShareLink",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/share-link`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Rotate the itinerary's share link.
 * POST /v1/itineraries/{itinerary_id}/share-link/rotate
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryShareLinkRotate = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryShareLinkRotate"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: shareLinkDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postItineraryShareLinkRotate",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/share-link/rotate`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a new waypoint within an itinerary.
 * POST /v1/itineraries/{itinerary_id}/waypoints
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postItineraryWaypoint = (transport: MountayaTransport, args: {
  readonly "itinerary_id": string;
  readonly body: operations["postItineraryWaypoint"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postItineraryWaypoint"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: itineraryWithDetailsDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postItineraryWaypoint",
    path: `/v1/itineraries/${encodeURIComponent(String(args.itinerary_id))}/waypoints`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Accept an invitation.
 * POST /v1/me/invitations/{invitation_id}/accept
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postMyInvitationAccept = (transport: MountayaTransport, args: {
  readonly "invitation_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postMyInvitationAccept"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: membershipDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postMyInvitationAccept",
    path: `/v1/me/invitations/${encodeURIComponent(String(args.invitation_id))}/accept`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create an organization.
 * POST /v1/organizations
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganization = (transport: MountayaTransport, args: {
  readonly body: operations["postOrganization"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganization"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postOrganization",
    path: "/v1/organizations",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Configure an organization's AI provider.
 * POST /v1/organizations/{organization_id}/ai-provider-config
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationAiProviderConfig = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["postOrganizationAiProviderConfig"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationAiProviderConfig"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postOrganizationAiProviderConfig",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/ai-provider-config`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Save a confirmed card against an organization.
 * POST /v1/organizations/{organization_id}/billing/payment-methods
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationBillingPaymentMethod = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["postOrganizationBillingPaymentMethod"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationBillingPaymentMethod"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postOrganizationBillingPaymentMethod",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/payment-methods`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Choose which payment method an organization is charged on.
 * POST /v1/organizations/{organization_id}/billing/payment-methods/{payment_method_id}/set-default
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationBillingPaymentMethodSetDefault = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly "payment_method_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationBillingPaymentMethodSetDefault"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postOrganizationBillingPaymentMethodSetDefault",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/payment-methods/${encodeURIComponent(String(args.payment_method_id))}/set-default`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Start collecting a card for an organization.
 * POST /v1/organizations/{organization_id}/billing/payment-methods/setup
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationBillingPaymentMethodSetup = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationBillingPaymentMethodSetup"]>> =>
  transport.request({
    actsAsMember: true,
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postOrganizationBillingPaymentMethodSetup",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/payment-methods/setup`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Open an organization's billing.
 * POST /v1/organizations/{organization_id}/billing/provision
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationBillingProvision = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["postOrganizationBillingProvision"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationBillingProvision"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postOrganizationBillingProvision",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/provision`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Run an organization's Data API subscription.
 * POST /v1/organizations/{organization_id}/billing/subscription
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationBillingSubscription = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationBillingSubscription"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: organizationSubscriptionSummaryDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    method: "POST",
    operationId: "postOrganizationBillingSubscription",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/billing/subscription`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Invite somebody to an organization by email.
 * POST /v1/organizations/{organization_id}/invitations
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationInvitation = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["postOrganizationInvitation"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationInvitation"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: organizationInvitationDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postOrganizationInvitation",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/invitations`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Resolve the organization a publishable key belongs to.
 * POST /v1/organizations/lookup
 * Takes no credential.
 */
export const postOrganizationLookup = (transport: MountayaTransport, args: {
  readonly body: operations["postOrganizationLookup"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationLookup"]>> =>
  transport.request({
    body: args.body,
    dates: { data: organizationDates },
    idempotent: true,
    method: "POST",
    operationId: "postOrganizationLookup",
    path: "/v1/organizations/lookup",
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesNoCredential: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a publishable key.
 * POST /v1/organizations/{organization_id}/publishable-keys
 * Requires a publishable key with the `management` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postOrganizationPublishableKey = (transport: MountayaTransport, args: {
  readonly "organization_id": string;
  readonly body: operations["postOrganizationPublishableKey"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postOrganizationPublishableKey"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: publishableKeyDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postOrganizationPublishableKey",
    path: `/v1/organizations/${encodeURIComponent(String(args.organization_id))}/publishable-keys`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a session token using a secret key.
 * POST /v1/sessions
 * Requires a secret key, and takes no other credential.
 */
export const postSession = (transport: MountayaTransport, args: {
  readonly body: operations["postSession"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
}, options?: RequestOptions): Promise<SuccessBody<operations["postSession"]>> =>
  transport.request({
    body: args.body,
    dates: { data: sessionDates },
    idempotent: true,
    method: "POST",
    operationId: "postSession",
    path: "/v1/sessions",
    requiresSecretKey: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Create a new zone.
 * POST /v1/zones
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postZone = (transport: MountayaTransport, args: {
  readonly body: operations["postZone"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postZone"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    dates: { data: zoneDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: false,
    method: "POST",
    operationId: "postZone",
    path: "/v1/zones",
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * Send a collaboration signal into a zone's session.
 * POST /v1/zones/{zone_id}/collab
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postZoneCollab = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
  readonly body: operations["postZoneCollab"]["requestBody"] extends { content: { "application/json": infer B } } ? B : never;
}, options?: RequestOptions): Promise<SuccessBody<operations["postZoneCollab"]>> =>
  transport.request({
    actsAsMember: true,
    body: args.body,
    idempotent: true,
    method: "POST",
    operationId: "postZoneCollab",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}/collab`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    timeoutMs: options?.timeoutMs,
  });

/**
 * Publish the zone's current draft.
 * POST /v1/zones/{zone_id}/publication
 * Requires a publishable key with the `studio-embedded` scope.
 * Acts as the user its session token was minted for, so the token must name one.
 */
export const postZonePublication = (transport: MountayaTransport, args: {
  readonly "zone_id": string;
  /** The `Idempotency-Key` every attempt sends; left out, the client draws one for the call. */
  readonly idempotencyKey?: string | undefined;
  /** The `etag` the resource was read under, sent as `If-Match`; left out, the write is unconditional. */
  readonly ifMatch?: string | undefined;
}, options?: RequestOptions): Promise<SuccessBody<operations["postZonePublication"]>> =>
  transport.request({
    actsAsMember: true,
    dates: { data: zoneDates },
    idempotencyKey: args.idempotencyKey,
    idempotent: true,
    ifMatch: args.ifMatch,
    method: "POST",
    operationId: "postZonePublication",
    path: `/v1/zones/${encodeURIComponent(String(args.zone_id))}/publication`,
    requiresSession: true,
    retry: options?.retry,
    serverBudgetMs: 30000,
    signal: options?.signal,
    surface: "rest",
    takesIdempotencyKey: true,
    timeoutMs: options?.timeoutMs,
  });

/**
 * bind returns every 'api' operation with the transport applied.
 */
export const bind = (transport: MountayaTransport) => ({
  admin: {
    billing: {
      /**
       * Detach a payment method from an organization.
       * DELETE /v1/organizations/{organization_id}/billing/payment-methods/{payment_method_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteOrganizationBillingPaymentMethod}
       */
      deleteOrganizationBillingPaymentMethod: (args: Parameters<typeof deleteOrganizationBillingPaymentMethod>[1], options?: RequestOptions) => deleteOrganizationBillingPaymentMethod(transport, args, options),
      /**
       * End an organization's Data API subscription at the end of its period.
       * DELETE /v1/organizations/{organization_id}/billing/subscription
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteOrganizationBillingSubscription}
       */
      deleteOrganizationBillingSubscription: (args: Parameters<typeof deleteOrganizationBillingSubscription>[1], options?: RequestOptions) => deleteOrganizationBillingSubscription(transport, args, options),
      /**
       * List the billing catalog.
       * GET /v1/billing/products
       * Requires a publishable key with the `management` scope.
       * @see {@link getBillingProducts}
       */
      getBillingProducts: (options?: RequestOptions) => getBillingProducts(transport, options),
      /**
       * Retrieve an organization's billing information.
       * GET /v1/organizations/{organization_id}/billing/info
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationBillingInfo}
       */
      getOrganizationBillingInfo: (args: Parameters<typeof getOrganizationBillingInfo>[1], options?: RequestOptions) => getOrganizationBillingInfo(transport, args, options),
      /**
       * List an organization's invoices.
       * GET /v1/organizations/{organization_id}/billing/invoices
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationBillingInvoices}
       */
      getOrganizationBillingInvoices: (args: Parameters<typeof getOrganizationBillingInvoices>[1], options?: RequestOptions) => getOrganizationBillingInvoices(transport, args, options),
      /**
       * List an organization's saved payment methods.
       * GET /v1/organizations/{organization_id}/billing/payment-methods
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationBillingPaymentMethods}
       */
      getOrganizationBillingPaymentMethods: (args: Parameters<typeof getOrganizationBillingPaymentMethods>[1], options?: RequestOptions) => getOrganizationBillingPaymentMethods(transport, args, options),
      /**
       * Check what a promotion code is worth to an organization.
       * GET /v1/organizations/{organization_id}/billing/promotion-codes/{code}
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationBillingPromotionCode}
       */
      getOrganizationBillingPromotionCode: (args: Parameters<typeof getOrganizationBillingPromotionCode>[1], options?: RequestOptions) => getOrganizationBillingPromotionCode(transport, args, options),
      /**
       * Retrieve an organization's subscription.
       * GET /v1/organizations/{organization_id}/billing/subscription
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationBillingSubscription}
       */
      getOrganizationBillingSubscription: (args: Parameters<typeof getOrganizationBillingSubscription>[1], options?: RequestOptions) => getOrganizationBillingSubscription(transport, args, options),
      /**
       * Update an organization's billing information.
       * PATCH /v1/organizations/{organization_id}/billing/info
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchOrganizationBillingInfo}
       */
      patchOrganizationBillingInfo: (args: Parameters<typeof patchOrganizationBillingInfo>[1], options?: RequestOptions) => patchOrganizationBillingInfo(transport, args, options),
      /**
       * Save a confirmed card against an organization.
       * POST /v1/organizations/{organization_id}/billing/payment-methods
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationBillingPaymentMethod}
       */
      postOrganizationBillingPaymentMethod: (args: Parameters<typeof postOrganizationBillingPaymentMethod>[1], options?: RequestOptions) => postOrganizationBillingPaymentMethod(transport, args, options),
      /**
       * Choose which payment method an organization is charged on.
       * POST /v1/organizations/{organization_id}/billing/payment-methods/{payment_method_id}/set-default
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationBillingPaymentMethodSetDefault}
       */
      postOrganizationBillingPaymentMethodSetDefault: (args: Parameters<typeof postOrganizationBillingPaymentMethodSetDefault>[1], options?: RequestOptions) => postOrganizationBillingPaymentMethodSetDefault(transport, args, options),
      /**
       * Start collecting a card for an organization.
       * POST /v1/organizations/{organization_id}/billing/payment-methods/setup
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationBillingPaymentMethodSetup}
       */
      postOrganizationBillingPaymentMethodSetup: (args: Parameters<typeof postOrganizationBillingPaymentMethodSetup>[1], options?: RequestOptions) => postOrganizationBillingPaymentMethodSetup(transport, args, options),
      /**
       * Open an organization's billing.
       * POST /v1/organizations/{organization_id}/billing/provision
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationBillingProvision}
       */
      postOrganizationBillingProvision: (args: Parameters<typeof postOrganizationBillingProvision>[1], options?: RequestOptions) => postOrganizationBillingProvision(transport, args, options),
      /**
       * Run an organization's Data API subscription.
       * POST /v1/organizations/{organization_id}/billing/subscription
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationBillingSubscription}
       */
      postOrganizationBillingSubscription: (args: Parameters<typeof postOrganizationBillingSubscription>[1], options?: RequestOptions) => postOrganizationBillingSubscription(transport, args, options),
    },
    invitations: {
      /**
       * Withdraw a pending invitation.
       * DELETE /v1/organizations/{organization_id}/invitations/{invitation_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteOrganizationInvitation}
       */
      deleteOrganizationInvitation: (args: Parameters<typeof deleteOrganizationInvitation>[1], options?: RequestOptions) => deleteOrganizationInvitation(transport, args, options),
      /**
       * Retrieve a pending invitation.
       * GET /v1/organizations/{organization_id}/invitations/{invitation_id}
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationInvitation}
       */
      getOrganizationInvitation: (args: Parameters<typeof getOrganizationInvitation>[1], options?: RequestOptions) => getOrganizationInvitation(transport, args, options),
      /**
       * List an organization's pending invitations.
       * GET /v1/organizations/{organization_id}/invitations
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationInvitations}
       */
      getOrganizationInvitations: (args: Parameters<typeof getOrganizationInvitations>[1], options?: RequestOptions) => getOrganizationInvitations(transport, args, options),
      /**
       * Invite somebody to an organization by email.
       * POST /v1/organizations/{organization_id}/invitations
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationInvitation}
       */
      postOrganizationInvitation: (args: Parameters<typeof postOrganizationInvitation>[1], options?: RequestOptions) => postOrganizationInvitation(transport, args, options),
    },
    keys: {
      /**
       * Revoke a publishable key.
       * DELETE /v1/organizations/{organization_id}/publishable-keys/{key_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteOrganizationPublishableKey}
       */
      deleteOrganizationPublishableKey: (args: Parameters<typeof deleteOrganizationPublishableKey>[1], options?: RequestOptions) => deleteOrganizationPublishableKey(transport, args, options),
      /**
       * Retrieve a publishable key.
       * GET /v1/organizations/{organization_id}/publishable-keys/{key_id}
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationPublishableKey}
       */
      getOrganizationPublishableKey: (args: Parameters<typeof getOrganizationPublishableKey>[1], options?: RequestOptions) => getOrganizationPublishableKey(transport, args, options),
      /**
       * List an organization's publishable keys.
       * GET /v1/organizations/{organization_id}/publishable-keys
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationPublishableKeys}
       */
      getOrganizationPublishableKeys: (args: Parameters<typeof getOrganizationPublishableKeys>[1], options?: RequestOptions) => getOrganizationPublishableKeys(transport, args, options),
      /**
       * Update a publishable key's name or scopes.
       * PATCH /v1/organizations/{organization_id}/publishable-keys/{key_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchOrganizationPublishableKey}
       */
      patchOrganizationPublishableKey: (args: Parameters<typeof patchOrganizationPublishableKey>[1], options?: RequestOptions) => patchOrganizationPublishableKey(transport, args, options),
      /**
       * Create a publishable key.
       * POST /v1/organizations/{organization_id}/publishable-keys
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationPublishableKey}
       */
      postOrganizationPublishableKey: (args: Parameters<typeof postOrganizationPublishableKey>[1], options?: RequestOptions) => postOrganizationPublishableKey(transport, args, options),
    },
    members: {
      /**
       * Remove a member from an organization, or leave it.
       * DELETE /v1/organizations/{organization_id}/members/{user_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteOrganizationMember}
       */
      deleteOrganizationMember: (args: Parameters<typeof deleteOrganizationMember>[1], options?: RequestOptions) => deleteOrganizationMember(transport, args, options),
      /**
       * List an organization's members.
       * GET /v1/organizations/{organization_id}/members
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationMembers}
       */
      getOrganizationMembers: (args: Parameters<typeof getOrganizationMembers>[1], options?: RequestOptions) => getOrganizationMembers(transport, args, options),
      /**
       * Change a member's role.
       * PATCH /v1/organizations/{organization_id}/members/{user_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchOrganizationMember}
       */
      patchOrganizationMember: (args: Parameters<typeof patchOrganizationMember>[1], options?: RequestOptions) => patchOrganizationMember(transport, args, options),
    },
    organizations: {
      /**
       * Delete an organization's AI provider configuration.
       * DELETE /v1/organizations/{organization_id}/ai-provider-config
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteOrganizationAiProviderConfig}
       */
      deleteOrganizationAiProviderConfig: (args: Parameters<typeof deleteOrganizationAiProviderConfig>[1], options?: RequestOptions) => deleteOrganizationAiProviderConfig(transport, args, options),
      /**
       * List the AI provider catalog.
       * GET /v1/ai-providers
       * Requires a publishable key with the `management` scope.
       * @see {@link getAiProviders}
       */
      getAiProviders: (options?: RequestOptions) => getAiProviders(transport, options),
      /**
       * Retrieve an organization.
       * GET /v1/organizations/{organization_id}
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getOrganization}
       */
      getOrganization: (args: Parameters<typeof getOrganization>[1], options?: RequestOptions) => getOrganization(transport, args, options),
      /**
       * Retrieve an organization's AI provider configuration.
       * GET /v1/organizations/{organization_id}/ai-provider-config
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationAiProviderConfig}
       */
      getOrganizationAiProviderConfig: (args: Parameters<typeof getOrganizationAiProviderConfig>[1], options?: RequestOptions) => getOrganizationAiProviderConfig(transport, args, options),
      /**
       * Retrieve an organization's Map Publishing settings.
       * GET /v1/organizations/{organization_id}/settings
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationSettings}
       */
      getOrganizationSettings: (args: Parameters<typeof getOrganizationSettings>[1], options?: RequestOptions) => getOrganizationSettings(transport, args, options),
      /**
       * Retrieve an organization's data usage.
       * GET /v1/organizations/{organization_id}/usage/data
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationUsageData}
       */
      getOrganizationUsageData: (args: Parameters<typeof getOrganizationUsageData>[1], options?: RequestOptions) => getOrganizationUsageData(transport, args, options),
      /**
       * Retrieve an organization's Tile API usage.
       * GET /v1/organizations/{organization_id}/usage/tiles
       * Requires a publishable key with the `management` scope.
       * @see {@link getOrganizationUsageTiles}
       */
      getOrganizationUsageTiles: (args: Parameters<typeof getOrganizationUsageTiles>[1], options?: RequestOptions) => getOrganizationUsageTiles(transport, args, options),
      /**
       * Update an organization.
       * PATCH /v1/organizations/{organization_id}
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchOrganization}
       */
      patchOrganization: (args: Parameters<typeof patchOrganization>[1], options?: RequestOptions) => patchOrganization(transport, args, options),
      /**
       * Update an organization's AI provider configuration.
       * PATCH /v1/organizations/{organization_id}/ai-provider-config
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchOrganizationAiProviderConfig}
       */
      patchOrganizationAiProviderConfig: (args: Parameters<typeof patchOrganizationAiProviderConfig>[1], options?: RequestOptions) => patchOrganizationAiProviderConfig(transport, args, options),
      /**
       * Update an organization's Map Publishing settings.
       * PATCH /v1/organizations/{organization_id}/settings
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchOrganizationSettings}
       */
      patchOrganizationSettings: (args: Parameters<typeof patchOrganizationSettings>[1], options?: RequestOptions) => patchOrganizationSettings(transport, args, options),
      /**
       * Create an organization.
       * POST /v1/organizations
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganization}
       */
      postOrganization: (args: Parameters<typeof postOrganization>[1], options?: RequestOptions) => postOrganization(transport, args, options),
      /**
       * Configure an organization's AI provider.
       * POST /v1/organizations/{organization_id}/ai-provider-config
       * Requires a publishable key with the `management` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postOrganizationAiProviderConfig}
       */
      postOrganizationAiProviderConfig: (args: Parameters<typeof postOrganizationAiProviderConfig>[1], options?: RequestOptions) => postOrganizationAiProviderConfig(transport, args, options),
      /**
       * Resolve the organization a publishable key belongs to.
       * POST /v1/organizations/lookup
       * Takes no credential.
       * @see {@link postOrganizationLookup}
       */
      postOrganizationLookup: (args: Parameters<typeof postOrganizationLookup>[1], options?: RequestOptions) => postOrganizationLookup(transport, args, options),
    },
    sessions: {
      /**
       * Create a session token using a secret key.
       * POST /v1/sessions
       * Requires a secret key, and takes no other credential.
       * @see {@link postSession}
       */
      postSession: (args: Parameters<typeof postSession>[1], options?: RequestOptions) => postSession(transport, args, options),
    },
    users: {
      /**
       * Decline an invitation.
       * DELETE /v1/me/invitations/{invitation_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteMyInvitation}
       */
      deleteMyInvitation: (args: Parameters<typeof deleteMyInvitation>[1], options?: RequestOptions) => deleteMyInvitation(transport, args, options),
      /**
       * List the invitations waiting for the acting user.
       * GET /v1/me/invitations
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getMyInvitations}
       */
      getMyInvitations: (args?: Parameters<typeof getMyInvitations>[1], options?: RequestOptions) => getMyInvitations(transport, args, options),
      /**
       * List the acting user's memberships.
       * GET /v1/me/organizations
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getMyOrganizations}
       */
      getMyOrganizations: (args?: Parameters<typeof getMyOrganizations>[1], options?: RequestOptions) => getMyOrganizations(transport, args, options),
      /**
       * Retrieve the acting user's display preferences.
       * GET /v1/me/preferences
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getMyPreferences}
       */
      getMyPreferences: (options?: RequestOptions) => getMyPreferences(transport, options),
      /**
       * Update the acting user's display preferences.
       * PATCH /v1/me/preferences
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchMyPreferences}
       */
      patchMyPreferences: (args: Parameters<typeof patchMyPreferences>[1], options?: RequestOptions) => patchMyPreferences(transport, args, options),
      /**
       * Accept an invitation.
       * POST /v1/me/invitations/{invitation_id}/accept
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postMyInvitationAccept}
       */
      postMyInvitationAccept: (args: Parameters<typeof postMyInvitationAccept>[1], options?: RequestOptions) => postMyInvitationAccept(transport, args, options),
    },
  },
  agent: {
    runs: {
      /**
       * Stream an agent run's events over Server-Sent Events.
       * GET /v1/agent/runs/{run_id}/events
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getAgentRunEvents}
       */
      getAgentRunEvents: (args: Parameters<typeof getAgentRunEvents>[1], options?: RequestOptions) => getAgentRunEvents(transport, args, options),
      /**
       * Start a new agent run.
       * POST /v1/agent/runs
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postAgentRun}
       */
      postAgentRun: (args: Parameters<typeof postAgentRun>[1], options?: RequestOptions) => postAgentRun(transport, args, options),
      /**
       * Record the user's rating of one assistant message in a run.
       * POST /v1/agent/runs/{run_id}/feedback
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postAgentRunFeedback}
       */
      postAgentRunFeedback: (args: Parameters<typeof postAgentRunFeedback>[1], options?: RequestOptions) => postAgentRunFeedback(transport, args, options),
      /**
       * Send an input signal into a running agent run.
       * POST /v1/agent/runs/{run_id}/input
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postAgentRunInput}
       */
      postAgentRunInput: (args: Parameters<typeof postAgentRunInput>[1], options?: RequestOptions) => postAgentRunInput(transport, args, options),
    },
  },
  studio: {
    activities: {
      /**
       * List all activity categories and their activities.
       * GET /v1/activities
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getActivities}
       */
      getActivities: (options?: RequestOptions) => getActivities(transport, options),
    },
    collab: {
      /**
       * Stream one itinerary's collaboration session over SSE.
       * GET /v1/itineraries/{itinerary_id}/events
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getItineraryEvents}
       */
      getItineraryEvents: (args: Parameters<typeof getItineraryEvents>[1], options?: RequestOptions) => getItineraryEvents(transport, args, options),
      /**
       * Stream an organization's list events over SSE.
       * GET /v1/organizations/{organization_id}/events
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getOrganizationEvents}
       */
      getOrganizationEvents: (args: Parameters<typeof getOrganizationEvents>[1], options?: RequestOptions) => getOrganizationEvents(transport, args, options),
      /**
       * Stream one zone's collaboration session over SSE.
       * GET /v1/zones/{zone_id}/events
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getZoneEvents}
       */
      getZoneEvents: (args: Parameters<typeof getZoneEvents>[1], options?: RequestOptions) => getZoneEvents(transport, args, options),
      /**
       * Send a collaboration signal into an itinerary's session.
       * POST /v1/itineraries/{itinerary_id}/collab
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryCollab}
       */
      postItineraryCollab: (args: Parameters<typeof postItineraryCollab>[1], options?: RequestOptions) => postItineraryCollab(transport, args, options),
      /**
       * Send a collaboration signal into a zone's session.
       * POST /v1/zones/{zone_id}/collab
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postZoneCollab}
       */
      postZoneCollab: (args: Parameters<typeof postZoneCollab>[1], options?: RequestOptions) => postZoneCollab(transport, args, options),
    },
    collections: {
      /**
       * Delete a collection.
       * DELETE /v1/collections/{collection_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteCollection}
       */
      deleteCollection: (args: Parameters<typeof deleteCollection>[1], options?: RequestOptions) => deleteCollection(transport, args, options),
      /**
       * Remove an itinerary from a collection.
       * DELETE /v1/collections/{collection_id}/itineraries/{itinerary_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteCollectionItinerary}
       */
      deleteCollectionItinerary: (args: Parameters<typeof deleteCollectionItinerary>[1], options?: RequestOptions) => deleteCollectionItinerary(transport, args, options),
      /**
       * Remove the collection's share link.
       * DELETE /v1/collections/{collection_id}/share-link
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteCollectionShareLink}
       */
      deleteCollectionShareLink: (args: Parameters<typeof deleteCollectionShareLink>[1], options?: RequestOptions) => deleteCollectionShareLink(transport, args, options),
      /**
       * Remove a zone from a collection.
       * DELETE /v1/collections/{collection_id}/zones/{zone_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteCollectionZone}
       */
      deleteCollectionZone: (args: Parameters<typeof deleteCollectionZone>[1], options?: RequestOptions) => deleteCollectionZone(transport, args, options),
      /**
       * Retrieve a single collection with its itineraries and zones.
       * GET /v1/collections/{collection_id}
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getCollection}
       */
      getCollection: (args: Parameters<typeof getCollection>[1], options?: RequestOptions) => getCollection(transport, args, options),
      /**
       * List the itineraries a collection contains.
       * GET /v1/collections/{collection_id}/itineraries
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getCollectionItineraries}
       */
      getCollectionItineraries: (args: Parameters<typeof getCollectionItineraries>[1], options?: RequestOptions) => getCollectionItineraries(transport, args, options),
      /**
       * List collections.
       * GET /v1/collections
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getCollections}
       */
      getCollections: (args?: Parameters<typeof getCollections>[1], options?: RequestOptions) => getCollections(transport, args, options),
      /**
       * Read the collection's share link.
       * GET /v1/collections/{collection_id}/share-link
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getCollectionShareLink}
       */
      getCollectionShareLink: (args: Parameters<typeof getCollectionShareLink>[1], options?: RequestOptions) => getCollectionShareLink(transport, args, options),
      /**
       * List the zones a collection contains.
       * GET /v1/collections/{collection_id}/zones
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getCollectionZones}
       */
      getCollectionZones: (args: Parameters<typeof getCollectionZones>[1], options?: RequestOptions) => getCollectionZones(transport, args, options),
      /**
       * Update an existing collection.
       * PATCH /v1/collections/{collection_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchCollection}
       */
      patchCollection: (args: Parameters<typeof patchCollection>[1], options?: RequestOptions) => patchCollection(transport, args, options),
      /**
       * Set an itinerary's position within a collection.
       * PATCH /v1/collections/{collection_id}/itineraries/{itinerary_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchCollectionItinerary}
       */
      patchCollectionItinerary: (args: Parameters<typeof patchCollectionItinerary>[1], options?: RequestOptions) => patchCollectionItinerary(transport, args, options),
      /**
       * Create a new collection.
       * POST /v1/collections
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postCollection}
       */
      postCollection: (args: Parameters<typeof postCollection>[1], options?: RequestOptions) => postCollection(transport, args, options),
      /**
       * Add itineraries to a collection.
       * POST /v1/collections/{collection_id}/itineraries
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postCollectionItineraries}
       */
      postCollectionItineraries: (args: Parameters<typeof postCollectionItineraries>[1], options?: RequestOptions) => postCollectionItineraries(transport, args, options),
      /**
       * Mint the collection's share link, or return the one it has.
       * POST /v1/collections/{collection_id}/share-link
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postCollectionShareLink}
       */
      postCollectionShareLink: (args: Parameters<typeof postCollectionShareLink>[1], options?: RequestOptions) => postCollectionShareLink(transport, args, options),
      /**
       * Rotate the collection's share link.
       * POST /v1/collections/{collection_id}/share-link/rotate
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postCollectionShareLinkRotate}
       */
      postCollectionShareLinkRotate: (args: Parameters<typeof postCollectionShareLinkRotate>[1], options?: RequestOptions) => postCollectionShareLinkRotate(transport, args, options),
      /**
       * Add zones to a collection.
       * POST /v1/collections/{collection_id}/zones
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postCollectionZones}
       */
      postCollectionZones: (args: Parameters<typeof postCollectionZones>[1], options?: RequestOptions) => postCollectionZones(transport, args, options),
    },
    itineraries: {
      /**
       * Delete an itinerary.
       * DELETE /v1/itineraries/{itinerary_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteItinerary}
       */
      deleteItinerary: (args: Parameters<typeof deleteItinerary>[1], options?: RequestOptions) => deleteItinerary(transport, args, options),
      /**
       * Take the itinerary's publication down.
       * DELETE /v1/itineraries/{itinerary_id}/publication
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteItineraryPublication}
       */
      deleteItineraryPublication: (args: Parameters<typeof deleteItineraryPublication>[1], options?: RequestOptions) => deleteItineraryPublication(transport, args, options),
      /**
       * Delete a segment from an itinerary.
       * DELETE /v1/itineraries/{itinerary_id}/segments/{segment_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteItinerarySegment}
       */
      deleteItinerarySegment: (args: Parameters<typeof deleteItinerarySegment>[1], options?: RequestOptions) => deleteItinerarySegment(transport, args, options),
      /**
       * Delete a segment group.
       * DELETE /v1/itineraries/{itinerary_id}/segment-groups/{group_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteItinerarySegmentGroup}
       */
      deleteItinerarySegmentGroup: (args: Parameters<typeof deleteItinerarySegmentGroup>[1], options?: RequestOptions) => deleteItinerarySegmentGroup(transport, args, options),
      /**
       * Remove the itinerary's share link.
       * DELETE /v1/itineraries/{itinerary_id}/share-link
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteItineraryShareLink}
       */
      deleteItineraryShareLink: (args: Parameters<typeof deleteItineraryShareLink>[1], options?: RequestOptions) => deleteItineraryShareLink(transport, args, options),
      /**
       * Delete a waypoint from an itinerary.
       * DELETE /v1/itineraries/{itinerary_id}/waypoints/{waypoint_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteItineraryWaypoint}
       */
      deleteItineraryWaypoint: (args: Parameters<typeof deleteItineraryWaypoint>[1], options?: RequestOptions) => deleteItineraryWaypoint(transport, args, options),
      /**
       * List the itineraries the credential reaches.
       * GET /v1/itineraries
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getItineraries}
       */
      getItineraries: (args?: Parameters<typeof getItineraries>[1], options?: RequestOptions) => getItineraries(transport, args, options),
      /**
       * Retrieve a single itinerary with its segments and waypoints.
       * GET /v1/itineraries/{itinerary_id}
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getItinerary}
       */
      getItinerary: (args: Parameters<typeof getItinerary>[1], options?: RequestOptions) => getItinerary(transport, args, options),
      /**
       * Export an itinerary as a downloadable file.
       * GET /v1/itineraries/{itinerary_id}/export
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getItineraryExport}
       */
      getItineraryExport: (args: Parameters<typeof getItineraryExport>[1], options?: RequestOptions) => getItineraryExport(transport, args, options),
      /**
       * Re-time an itinerary at a travel pace the caller names.
       * GET /v1/itineraries/{itinerary_id}/pace
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getItineraryPace}
       */
      getItineraryPace: (args: Parameters<typeof getItineraryPace>[1], options?: RequestOptions) => getItineraryPace(transport, args, options),
      /**
       * Read the itinerary's share link.
       * GET /v1/itineraries/{itinerary_id}/share-link
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link getItineraryShareLink}
       */
      getItineraryShareLink: (args: Parameters<typeof getItineraryShareLink>[1], options?: RequestOptions) => getItineraryShareLink(transport, args, options),
      /**
       * Update an existing itinerary.
       * PATCH /v1/itineraries/{itinerary_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchItinerary}
       */
      patchItinerary: (args: Parameters<typeof patchItinerary>[1], options?: RequestOptions) => patchItinerary(transport, args, options),
      /**
       * Update an existing segment.
       * PATCH /v1/itineraries/{itinerary_id}/segments/{segment_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchItinerarySegment}
       */
      patchItinerarySegment: (args: Parameters<typeof patchItinerarySegment>[1], options?: RequestOptions) => patchItinerarySegment(transport, args, options),
      /**
       * Update a segment group.
       * PATCH /v1/itineraries/{itinerary_id}/segment-groups/{group_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchItinerarySegmentGroup}
       */
      patchItinerarySegmentGroup: (args: Parameters<typeof patchItinerarySegmentGroup>[1], options?: RequestOptions) => patchItinerarySegmentGroup(transport, args, options),
      /**
       * Update an existing waypoint.
       * PATCH /v1/itineraries/{itinerary_id}/waypoints/{waypoint_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchItineraryWaypoint}
       */
      patchItineraryWaypoint: (args: Parameters<typeof patchItineraryWaypoint>[1], options?: RequestOptions) => patchItineraryWaypoint(transport, args, options),
      /**
       * Create a new itinerary.
       * POST /v1/itineraries
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItinerary}
       */
      postItinerary: (args: Parameters<typeof postItinerary>[1], options?: RequestOptions) => postItinerary(transport, args, options),
      /**
       * Duplicate an itinerary into the key's organization.
       * POST /v1/itineraries/{itinerary_id}/duplicate
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryDuplicate}
       */
      postItineraryDuplicate: (args: Parameters<typeof postItineraryDuplicate>[1], options?: RequestOptions) => postItineraryDuplicate(transport, args, options),
      /**
       * Confirm an itinerary import, persisting the reviewed draft.
       * POST /v1/itineraries/import
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryImport}
       */
      postItineraryImport: (args: Parameters<typeof postItineraryImport>[1], options?: RequestOptions) => postItineraryImport(transport, args, options),
      /**
       * Preview an itinerary import without persisting it.
       * POST /v1/itineraries/preview
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryPreview}
       */
      postItineraryPreview: (args: Parameters<typeof postItineraryPreview>[1], options?: RequestOptions) => postItineraryPreview(transport, args, options),
      /**
       * Publish the itinerary's current draft.
       * POST /v1/itineraries/{itinerary_id}/publication
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryPublication}
       */
      postItineraryPublication: (args: Parameters<typeof postItineraryPublication>[1], options?: RequestOptions) => postItineraryPublication(transport, args, options),
      /**
       * Create a segment, at the end of the chain or before another.
       * POST /v1/itineraries/{itinerary_id}/segments
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItinerarySegment}
       */
      postItinerarySegment: (args: Parameters<typeof postItinerarySegment>[1], options?: RequestOptions) => postItinerarySegment(transport, args, options),
      /**
       * Convert a segment between routed and freehand.
       * POST /v1/itineraries/{itinerary_id}/segments/{segment_id}/convert
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItinerarySegmentConvert}
       */
      postItinerarySegmentConvert: (args: Parameters<typeof postItinerarySegmentConvert>[1], options?: RequestOptions) => postItinerarySegmentConvert(transport, args, options),
      /**
       * Group a contiguous run of an itinerary's segments.
       * POST /v1/itineraries/{itinerary_id}/segment-groups
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItinerarySegmentGroup}
       */
      postItinerarySegmentGroup: (args: Parameters<typeof postItinerarySegmentGroup>[1], options?: RequestOptions) => postItinerarySegmentGroup(transport, args, options),
      /**
       * Merge a segment with the one that follows it.
       * POST /v1/itineraries/{itinerary_id}/segments/{segment_id}/merge
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItinerarySegmentMerge}
       */
      postItinerarySegmentMerge: (args: Parameters<typeof postItinerarySegmentMerge>[1], options?: RequestOptions) => postItinerarySegmentMerge(transport, args, options),
      /**
       * Split a segment in two at a fraction of its length.
       * POST /v1/itineraries/{itinerary_id}/segments/{segment_id}/split
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItinerarySegmentSplit}
       */
      postItinerarySegmentSplit: (args: Parameters<typeof postItinerarySegmentSplit>[1], options?: RequestOptions) => postItinerarySegmentSplit(transport, args, options),
      /**
       * Mint the itinerary's share link, or return the one it has.
       * POST /v1/itineraries/{itinerary_id}/share-link
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryShareLink}
       */
      postItineraryShareLink: (args: Parameters<typeof postItineraryShareLink>[1], options?: RequestOptions) => postItineraryShareLink(transport, args, options),
      /**
       * Rotate the itinerary's share link.
       * POST /v1/itineraries/{itinerary_id}/share-link/rotate
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryShareLinkRotate}
       */
      postItineraryShareLinkRotate: (args: Parameters<typeof postItineraryShareLinkRotate>[1], options?: RequestOptions) => postItineraryShareLinkRotate(transport, args, options),
      /**
       * Create a new waypoint within an itinerary.
       * POST /v1/itineraries/{itinerary_id}/waypoints
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postItineraryWaypoint}
       */
      postItineraryWaypoint: (args: Parameters<typeof postItineraryWaypoint>[1], options?: RequestOptions) => postItineraryWaypoint(transport, args, options),
    },
    layers: {
      /**
       * List terrains, overlays, and the zone layer.
       * GET /v1/layers
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getLayers}
       */
      getLayers: (args?: Parameters<typeof getLayers>[1], options?: RequestOptions) => getLayers(transport, args, options),
    },
    zones: {
      /**
       * Delete a zone.
       * DELETE /v1/zones/{zone_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteZone}
       */
      deleteZone: (args: Parameters<typeof deleteZone>[1], options?: RequestOptions) => deleteZone(transport, args, options),
      /**
       * Take the zone's publication down.
       * DELETE /v1/zones/{zone_id}/publication
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link deleteZonePublication}
       */
      deleteZonePublication: (args: Parameters<typeof deleteZonePublication>[1], options?: RequestOptions) => deleteZonePublication(transport, args, options),
      /**
       * Retrieve a single zone with its geometry.
       * GET /v1/zones/{zone_id}
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getZone}
       */
      getZone: (args: Parameters<typeof getZone>[1], options?: RequestOptions) => getZone(transport, args, options),
      /**
       * List zones.
       * GET /v1/zones
       * Requires a publishable key with the `publishing` scope, or the `studio-embedded` scope that includes it.
       * @see {@link getZones}
       */
      getZones: (args?: Parameters<typeof getZones>[1], options?: RequestOptions) => getZones(transport, args, options),
      /**
       * Update an existing zone.
       * PATCH /v1/zones/{zone_id}
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link patchZone}
       */
      patchZone: (args: Parameters<typeof patchZone>[1], options?: RequestOptions) => patchZone(transport, args, options),
      /**
       * Create a new zone.
       * POST /v1/zones
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postZone}
       */
      postZone: (args: Parameters<typeof postZone>[1], options?: RequestOptions) => postZone(transport, args, options),
      /**
       * Publish the zone's current draft.
       * POST /v1/zones/{zone_id}/publication
       * Requires a publishable key with the `studio-embedded` scope.
       * Acts as the user its session token was minted for, so the token must name one.
       * @see {@link postZonePublication}
       */
      postZonePublication: (args: Parameters<typeof postZonePublication>[1], options?: RequestOptions) => postZonePublication(transport, args, options),
    },
  },
});
